import { useState, useEffect } from "react";
import { api } from "../../context";

export default function Engagement({ t }) {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const CARD=t.card,BORDER=t.border,TEXT=t.text,SUB=t.sub,PRI=t.pri,GRN=t.grn,RED=t.red,GOLD=t.gold;
  const fmt = (n) => Number(n || 0).toLocaleString();

  useEffect(() => {
    Promise.all([api("/admin/dashboard"), api("/admin/analytics")]).then(([d, a]) => {
      if (d.ok) setData(d.data);
      if (a.ok) setAnalytics(a.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ padding: 40, color: SUB, textAlign: "center" }}>⏳ Loading engagement data...</div>;

  const MiniBar = ({ items, color, height = 60, valueKey = "count" }) => {
    if (!items?.length) return <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: SUB, fontSize: 12 }}>No data yet</div>;
    const max = Math.max(...items.map(d => d[valueKey] || 0), 1);
    return (
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height }}>
        {items.map((d, i) => {
          const h = Math.max(2, ((d[valueKey] || 0) / max) * (height - 4));
          return <div key={i} style={{ flex: 1, height: h, background: color, borderRadius: "2px 2px 0 0", opacity: 0.85, minWidth: 3 }} title={`${d.date}: ${d[valueKey]}`} />;
        })}
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: TEXT, marginBottom: 4 }}>🤝 Engagement</div>
      <div style={{ fontSize: 12, color: SUB, marginBottom: 24 }}>User activity, retention and platform health</div>

      {/* DAU/WAU/MAU */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "DAU (Today)",  value: fmt(data?.active_today),  color: GRN,   sub: "Daily Active Users" },
          { label: "WAU (7 days)", value: fmt(data?.active_week),   color: PRI,   sub: "Weekly Active Users" },
          { label: "MAU (30 days)",value: fmt(data?.active_month),  color: GOLD,  sub: "Monthly Active Users" },
          { label: "Total Members",value: fmt(data?.total_users),   color: "#8b5cf6", sub: "All time" },
        ].map((s, i) => (
          <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: SUB, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: SUB, marginTop: 4 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Retention */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {[
          { label: "7-Day Retention",  value: data?.retention_7 || 0 },
          { label: "30-Day Retention", value: data?.retention_30 || 0 },
        ].map((r, i) => {
          const color = r.value >= 50 ? GRN : r.value >= 25 ? GOLD : RED;
          return (
            <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>{r.label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color }}>{r.value}%</div>
              </div>
              <div style={{ height: 10, borderRadius: 5, background: BORDER }}>
                <div style={{ height: 10, borderRadius: 5, background: color, width: `${r.value}%`, transition: "width 1s" }} />
              </div>
              <div style={{ fontSize: 12, color: SUB, marginTop: 8 }}>
                {r.value >= 50 ? "✅ Excellent — users love the platform!" : r.value >= 25 ? "⚠️ Needs improvement — focus on engagement features" : "❌ Critical — users are not coming back"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Activity charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
        {[
          { label: "Daily Active Users (30d)", data: analytics?.active, color: GRN },
          { label: "Daily Signups (30d)", data: analytics?.signups, color: PRI },
          { label: "Daily Messages (30d)", data: analytics?.messages, color: "#06b6d4" },
        ].map((chart, i) => (
          <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 16 }}>{chart.label}</div>
            <MiniBar items={chart.data} color={chart.color} height={70} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 10, color: SUB }}>{chart.data?.[0]?.date?.slice(5) || ""}</span>
              <span style={{ fontSize: 10, color: SUB }}>{chart.data?.[chart.data?.length - 1]?.date?.slice(5) || ""}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Posts + Content */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>📝 Content Activity</div>
          {[
            { label: "Total Posts",    value: fmt(data?.total_posts),   color: "#8b5cf6" },
            { label: "Posts Today",    value: fmt(data?.posts_today),   color: PRI },
            { label: "Total Messages", value: fmt(data?.total_messages),color: "#06b6d4" },
            { label: "Messages Today", value: fmt(data?.msgs_today),    color: GRN },
            { label: "Open Reports",   value: fmt(data?.open_reports),  color: RED },
          ].map((s, i, arr) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < arr.length - 1 ? `1px solid ${BORDER}` : "none" }}>
              <span style={{ fontSize: 13, color: SUB }}>{s.label}</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>

        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🏆 Most Active Users (7 days)</div>
          {!(analytics?.top_active?.length)
            ? <div style={{ textAlign: "center", padding: "24px 0", color: SUB, fontSize: 13 }}>No activity data yet</div>
            : analytics.top_active.slice(0, 8).map((u, i) => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < 7 ? `1px solid ${BORDER}` : "none" }}>
                <span style={{ fontSize: 11, color: SUB, width: 18, fontWeight: 700 }}>{i + 1}</span>
                <div style={{ width: 30, height: 30, borderRadius: 15, background: `linear-gradient(135deg, ${PRI}, #a23ad1)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{u.avatar_emoji || "👤"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: TEXT }}>{u.name}</div>
                  <div style={{ fontSize: 10, color: SUB }}>{u.posts_count} posts · {u.dm_count} DMs</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: GOLD }}>★{u.trust_score}</div>
              </div>
            ))
          }
        </div>
      </div>

      {/* Plan breakdown */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>💳 Membership Breakdown</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {[
            { label: "Free", count: analytics?.plans?.free || 0, color: SUB, icon: "🆓" },
            { label: "Premium", count: analytics?.plans?.premium || 0, color: PRI, icon: "💎" },
            { label: "Elite", count: analytics?.plans?.elite || 0, color: GOLD, icon: "👑" },
          ].map((p, i) => {
            const total = (analytics?.plans?.free || 0) + (analytics?.plans?.premium || 0) + (analytics?.plans?.elite || 0);
            const pct = total > 0 ? Math.round(p.count / total * 100) : 0;
            return (
              <div key={i} style={{ background: t.input, borderRadius: 12, padding: 16, textAlign: "center", border: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{p.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: p.color }}>{p.count}</div>
                <div style={{ fontSize: 12, color: SUB, marginTop: 4 }}>{p.label} · {pct}%</div>
                <div style={{ height: 6, borderRadius: 3, background: BORDER, marginTop: 10 }}>
                  <div style={{ height: 6, borderRadius: 3, background: p.color, width: `${pct}%`, transition: "width 0.8s" }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Verification stats */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🛡️ Verification Status</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {[
            { label: "Verified", count: analytics?.verification?.verified || 0, color: GRN },
            { label: "Unverified", count: analytics?.verification?.unverified || 0, color: RED },
            { label: "Pending", count: analytics?.verification?.pending || 0, color: GOLD },
          ].map((v, i) => (
            <div key={i} style={{ background: `${v.color}10`, borderRadius: 12, padding: 16, textAlign: "center", border: `1px solid ${v.color}25` }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: v.color }}>{v.count}</div>
              <div style={{ fontSize: 12, color: SUB, marginTop: 6 }}>{v.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}